import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

interface PageContent {
  [key: string]: any;
}

export const usePageContent = () => {
  const [content, setContent] = useState<PageContent>(getDefaultContent());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchContent = async () => {
      try {
        const { data, error } = await supabase
          .from('page_content')
          .select('*')
          .abortSignal(abortController.signal);

        if (error) throw error;

        if (data && data.length > 0) {
          const contentMap: PageContent = {};
          data.forEach(item => {
            contentMap[item.section_key] = item.content;
          });
          setContent(contentMap);
        }
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error fetching content:', error);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchContent();

    return () => {
      abortController.abort();
    };
  }, []);

  return { content, loading };
};

const getDefaultContent = (): PageContent => ({
  hero: {
    title: "Inner Wealth for Those Who Already Have Enough — Yet Don't Feel It",
    subtitle: "Private inner wealth coaching for financially capable, high-performing individuals who want to feel grounded, fulfilled, and at home within themselves — not just successful on the outside.",
    cta: "Apply for a Complimentary 30-Minute Call"
  },
  about: {
    title: "About Stefi",
    paragraphs: [
      "I work with people who already appear successful, yet feel internally disconnected or under-fulfilled.",
      "My work is grounded, calm, and deeply relational. I don't push transformation — I create the conditions where it becomes possible.",
      "Clients often describe feeling safer in themselves, clearer in their choices, and more sovereign in their relationships.",
      "I work privately and selectively, because depth requires space, trust, and commitment."
    ],
    quote: "When inner wealth is restored, everything reorganises naturally: your energy, your choices, your relationships, and your sense of peace."
  },
  shift: {
    title: "The Inner Wealth Shift",
    subtitle: "Inner wealth is not about making more money. It is about:",
    items: [
      "feeling enough without proving",
      "safety in your own nervous system",
      "self-trust that guides decisions calmly",
      "boundaries that don't require guilt",
      "relationships that feel alive, not transactional",
      "sovereignty — internally and externally"
    ]
  },
  problem: {
    title: "The Unseen Problem",
    intro: [
      "You may already have money, success, or access to wealth — yet something inside still feels tight, restless, or unsatisfied.",
      "Many of my clients come to me not because their life is failing, but because it looks successful while feeling strangely empty."
    ],
    struggles_title: "They are capable, responsible, often generous — yet they struggle to:",
    struggles: [
      "truly rest or receive",
      "feel emotionally safe and \"enough\"",
      "experience depth in relationships",
      "trust themselves fully",
      "enjoy what they've built without inner pressure"
    ],
    conclusion: "This is not a mindset problem. It is an inner wealth gap."
  },
  offer: {
    title: "The Offer",
    subtitle: "3-Month Private Inner Wealth Transformation",
    description: "This is private, 1:1 work for individuals who are ready for depth, honesty, and responsibility for their inner world.",
    not_list: [
      "motivational coaching",
      "mindset hacking",
      "performance pushing",
      "quick fixes"
    ],
    is_list: [
      "deep identity recalibration",
      "nervous-system regulation",
      "emotional maturity and self-trust",
      "restoring a grounded relationship with yourself"
    ],
    investment: "£6,000+",
    duration: "3 Months",
    format: "1:1"
  },
  who_for: {
    title: "Who This Is For — And Who It Is Not",
    for_you: [
      "you are financially capable or wealthy",
      "you are emotionally intelligent and self-reflective",
      "you are willing to invest in real change",
      "you value depth over performance",
      "you are ready to stop outsourcing your sense of worth"
    ],
    not_for_you: [
      "you are in financial survival",
      "you want motivation, fixing, or urgency",
      "you are looking for guarantees",
      "you are not willing to take responsibility for yourself"
    ]
  },
  cta: {
    title: "Apply for a Complimentary 30-Minute Call",
    description1: "This call is not a sales conversation. It is a mutual exploration of whether this work is the right fit — for both of us.",
    description2: "The application helps ensure that our time is respected and aligned.",
    button: "Apply for a Complimentary 30-Minute Call"
  },
  application: {
    questions: [
      "What prompted you to apply at this point in your life?",
      "What feels unresolved or unsatisfying despite your external success?",
      "Are you willing and able to invest £6,000+ in a private 3-month transformation if this feels aligned?",
      "What outcome would make this work genuinely meaningful for you?"
    ]
  }
});
